import { invoker } from 'ramda';
import {
    paramDsName,
    paramSeparator,
    paramSpace,
    paramXPath,
} from '../constants/parameterNames';
import {
    typeDataSource,
    typeOption,
    typeXPath,
} from '../constants/parameterTypes';
import { failure } from '../helpers/commandResult';
import { getParamValue } from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNodeSet } from '../helpers/qRules.node.helpers';

const SPACE_BEFORE = 'before';
const SPACE_AFTER = 'after';
const SPACE_BOTH = 'both';

const DEFAULT_SEPARATOR = ';';

const optionalParameters = [
    {
        name: paramDsName,
        description:
            'Name of the data source that has the field values to delimit',
        type: typeDataSource,
    },
    {
        name: paramSeparator,
        description: `(default: ${DEFAULT_SEPARATOR}) Delimiter to place between the selected values`,
    },
    {
        name: paramSpace,
        description: `When specified, includes a space around the separator at the indicated position - possible values: ${SPACE_BEFORE}, ${SPACE_AFTER}, ${SPACE_BOTH}`,
        type: typeOption,
        options: [
            {
                name: 'Before',
                value: SPACE_BEFORE,
            },
            {
                name: 'After',
                value: SPACE_AFTER,
            },
            {
                name: 'Both',
                value: SPACE_BOTH,
            },
        ],
    },
];

const requiredParameters = [
    {
        name: paramXPath,
        description: 'XPath to the values to delimit',
        type: typeXPath,
        dsParentName: paramDsName,
    },
];

const description =
    'Concatenates the values of a repeating field (or field in a repeating group), separating each value with the specified delimiter.';

const getFullSeparator = (separator, space) => {
    if (!space) {
        return separator;
    }

    switch (space.toLowerCase()) {
        case SPACE_BEFORE:
            return ` ${separator}`;

        case SPACE_AFTER:
            return `${separator} `;

        case SPACE_BOTH:
            return ` ${separator} `;

        default:
            throw new Error(`Invalid /${paramSpace} value: ${space}`);
    }
};

function execute({ dataSources, parameters }) {
    const dsName = getParamValue(parameters, paramDsName);
    const xpath = getParamValue(parameters, paramXPath);
    const separator = getParamValue(
        parameters,
        paramSeparator,
        DEFAULT_SEPARATOR,
    );
    const space = getParamValue(parameters, paramSpace);

    if (!separator) {
        return failure(`/${paramSeparator} cannot be blank.`);
    }

    const fullSeparator = getFullSeparator(separator, space);

    const ds = getDataSource(dataSources, dsName);
    const nodes = getValidNodeSet(ds, xpath);

    try {
        return nodes.map(invoker(0, 'value')).join(fullSeparator);
    } catch (e) {
        console.error(e);

        return failure(`Unexpected error: ${e.message}`);
    }
}

const DelimitedList = {
    description,
    optionalParameters,
    requiredParameters,
    execute,
};

export default DelimitedList;
